import {
	btn,
	checkbox, column,
	comp,
	DataSourceForm,
	datasourceform, DefaultEntity,
	displayfield, EntityID, Format,
	h2,
	h3, hr,
	menu, mstbar, Notifier, searchbtn,
	t,
	tbar, Window, Filter
} from "@intermesh/goui";
import {DetailPanel, jmapds} from "@intermesh/groupoffice-core";
import {DnsSettingsPanel} from "./DnsSettingsPanel.js";
import {MailboxTable} from "./MailboxTable.js";
import {MailboxDialog} from "./MailboxDialog.js";
import {AliasDialog} from "./AliasDialog.js";
import {AliasTable} from "./AliasTable.js";
import {MailboxExportDialog} from "./MailboxExportDialog.js";
import {MailDomain, mailDomainStatus} from "./MailDomain.js";
import {DomainDialog} from "./DomainDialog.js";

export class DomainDetail extends DetailPanel<MailDomain> {
	private form: DataSourceForm<MailDomain>;
	private dnsSettingsForm: DnsSettingsPanel;
	private aliasTable!: AliasTable;
	private mailboxTable!: MailboxTable;

	constructor() {
		super("MailDomain");


		this.scroller.items.add(
			this.form = datasourceform({
				dataSource: jmapds("MailDomain")
			},

				comp({cls: "card "},
					tbar({},
						displayfield({
							escapeValue: false,
							name: "active",
							value: false,
							renderer: (v, field) => {
								if(!this.entity) {
									return "";
								}
								return mailDomainStatus(this.entity);
							}
						}),
						comp({},
							displayfield({
								tagName: "h3",
								name: "domain"
							}),

							displayfield({
								tagName: "small",
								name: "description"
							}),
						),

					),

					comp({cls: "pad flow"},
						displayfield({
							label: t("User"),
							name: "userId",
							renderer: async (userId) => {
								return jmapds("Principal").single(userId).then(u => u?.name ?? t("Not found"));
							}
						}),

						displayfield({
							label: t("Quota"),
							name: "totalQuota",
							flex: 1,
							renderer: (v) => {
								if(v === 0) {
									return t("Unlimited");
								}
								return Format.fileSize(v);
							}
						}),

						displayfield({
							flex: 1,
							label: t("Used quota"),
							name: "sumUsedQuota",
							renderer: (v, _record) => {
								return (v > 0) ? Format.fileSize(v) : "0B";
							}
						}),

						displayfield({
							flex: 1,
							label: t("Usage"),
							name: "sumUsage",
							width: 100,
							renderer: (v, _record) => {
								return (v > 0) ? Format.fileSize(v) : "0B";
							},
							hidden: true

						}),
					)
				),

				comp({cls: "card "},
					tbar({},
						h3("DNS Settings"),
						"->",
						btn({
							icon: "refresh",
							handler: async (btn) => {
								const card = btn.parent!.parent!;
								card.mask();

								try {
									await jmapds("MailDomain").update(this.entity!.id, {
										checkDNS: true
									})

									Notifier.success(t("DNS check completed"));
								} catch(e) {
									Window.error(e);
								} finally {
									card.unmask();
								}
							}
						})
					),
					this.dnsSettingsForm = new DnsSettingsPanel()
				),

				this.createMailboxesTab(),

				this.createAliasTab()
			)

		)


		this.toolbar.items.add(

			btn({
				icon: "edit",
				title: t("Edit"),
				handler: (button, ev) => {
					const win = new DomainDialog()
					win.load(this.entity!.id);
					win.show();
				}
			}),


			btn({
				icon: "more_vert",
				menu: menu({},

					btn({
						icon: "import_export",
						text: t("Export"),
						handler: async (_btn) => {
							void this.openExportDlg()

						}
					}),
					hr(),
					btn({
						icon: "delete",
						text: t("Delete"),
						handler: () => {
							jmapds("MailDomain").confirmDestroy([this.entity!.id]);
						}
					})
					)
			})
		)

		this.on("load",({entity}) => {
			void this.form.load(entity.id);

			this.mailboxTable.store.setFilter("domainId", {domainId: entity.id})
			void this.mailboxTable.store.load();

			this.aliasTable.store.setFilter("domainId", {domainId: entity.id})
			void this.aliasTable.store.load();

			this.dnsSettingsForm.domain = entity;
		});

		this.on("reset", () => {
			this.form.reset();
		});
	}


	private createMailboxesTab() {
		const mailboxTable = new MailboxTable();

		mailboxTable.on("rowdblclick", async ( {storeIndex}) => {
			await this.openMailboxDlg(mailboxTable.store.get(storeIndex)!.id);
		});

		mailboxTable.on("delete", async ({target}) => {
			const ids = target.rowSelection!.getSelected().map(row => row.id);
			await jmapds("MailBox")
				.confirmDestroy(ids);
		});

		this.mailboxTable = mailboxTable;

		return comp({
				cls: "card scroll fit"
			},

			tbar({cls: "border-bottom"},
				h3(t("Mailboxes")),
				'->',
				searchbtn({
					listeners: {
						input: ({text}) => {
							(mailboxTable!.store.queryParams.filter as Filter).text = text;
							void mailboxTable!.store.load();
						}
					}
				}),
				btn({
					cls: "primary filled",
					icon: "add",
					text: t("Add"),
					handler: async (_btn) => {
						await this.openMailboxDlg();
					}
				}),

				mstbar({table: mailboxTable},
					"->",
					btn({
						icon: "delete",
						handler: async (btn) => {

							const ids = mailboxTable.rowSelection!.getSelected().map(row =>row.record.id);

							const result = await jmapds("Mailbox")
								.confirmDestroy(ids);

							if(result != false) {
								btn.parent!.hide();
							}

						}
					})
				)


			), mailboxTable
		);


	}

	private async openMailboxDlg(id?: EntityID): Promise<void> {
		const dlg = new MailboxDialog();

		const d = this.form.value;

		dlg.form.value = {
			domainId: d.id,
			domain: this.form.findField("domain")!.value,
			quota:  d.defaultQuota
		};
		dlg.show();
		if (id) {
			await dlg.load(id);
		}
	}



	private createAliasTab() {

		const aliasTable = new AliasTable();
		aliasTable.on("rowdblclick", async ({storeIndex}) => {
			await this.openAliasDlg(aliasTable.store.get(storeIndex)!.id);
		});

		aliasTable.on("delete", async ({target}) => {
			const ids = target.rowSelection!.getSelected().map(row => row.id);
			await jmapds("MailAlias")
				.confirmDestroy(ids);
		});

		this.aliasTable = aliasTable;

		return comp({
				cls: "card scroll fit"
			},
			tbar({cls: "border-bottom"},
				h3(t("Aliases")),
				'->',
				searchbtn({
					listeners: {
						input: ( {text}) => {
							(aliasTable!.store.queryParams.filter as Filter).text = text;
							aliasTable!.store.load();
						}
					}
				}),
				btn({
					cls: "primary filled",
					icon: "add",
					text: t("Add"),
					handler: async (_btn) => {
						await this.openAliasDlg();
					}
				}),

				mstbar({table: aliasTable},
					"->",
					btn({
						icon: "delete",
						handler: async (btn) => {

							const ids = aliasTable.rowSelection!.getSelected().map(row => row.record.id);

							const result = await jmapds("Alias")
								.confirmDestroy(ids);

							if(result != false) {
								btn.parent!.hide();
							}

						}
					})
				)



			), aliasTable
		);
	}

	private async openAliasDlg(id?: EntityID): Promise<void> {
		const dlg = new AliasDialog();

		const d = this.form.value;
		dlg.form.value = {
			domainId: d.id,
			domain: this.form.findField("domain")!.value
		}
		dlg.show();
		if (id) {
			await dlg.load(id);
		}
	}


	private async openExportDlg(): Promise<void> {
		const ids = Array.from(this.mailboxTable.store.data, (m: DefaultEntity) => m.id);
		const w = new MailboxExportDialog();
		w.load(this.form.value, ids);
		w.show();
	}

}